/**
 * S-13 "Fans & Haters" (V7 — #36/#37/#14) — v3 "AFTER-DARK ARCADE" restyle.
 *
 * Two tabs over GET /likes/fans + /likes/haters:
 *  - Fans: like-family verdicts on you, topped by the Recent-viewers rail
 *    (#14 — viewed you in 30 d, no verdict; exclusions enforced server-side).
 *  - Haters: plain REJECT verdicts ONLY ("your haters await") — landed Super
 *    Rejects never surface here, premium included (SM-2 anonymity invariant).
 *
 * Free tier gets counts + blur (#37): the server sends blurred:true with
 * identities ABSENT from the payload, so the fog-of-war teaser below renders
 * no real data by construction — big neon counts, silhouettes under fog, a
 * lock. Tempting, honest, empty. A `premium_changed` push (SM-10b, EC-38)
 * re-fetches — lapse re-blurs, upgrade un-blurs; the server re-evaluates per
 * request regardless of what this client renders.
 *
 * Acting from the grid resolves the pair instantly (SM-1) through the SAME
 * POST /swipes path as the deck (shared useSwipeAction hook). All e2e
 * selectors preserved (heading, role=tab names, no blur_teaser class in the
 * premium view).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCopy, resolveCopy } from "@humble/shared";
import type { ApiComponents } from "@humble/shared";
import {
  Button,
  Card,
  IconBrokenHeart,
  Mascot,
  rise,
  Sheet,
  Skeleton,
  stagger,
  toast,
  VerifiedBadge,
} from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { useSwipeAction, type SwipeResult } from "../deck/use-swipe.js";
import "../deck/deck.css";
import "./likes.css";

type GridPage = ApiComponents["schemas"]["GridPage"];
type GridEntry = NonNullable<GridPage["entries"]>[number];
type UserRef = ApiComponents["schemas"]["UserRef"];
type Tab = "fans" | "haters";

function fetchPage(which: Tab, cursor?: string): Promise<GridPage> {
  const opts = cursor ? { query: { cursor } } : undefined;
  return which === "fans" ? api.get("/likes/fans", opts) : api.get("/likes/haters", opts);
}

function Head({ lede }: { lede?: string }) {
  return (
    <header className="likes__head">
      <p className="h-caps likes__kicker">The audience</p>
      <h1 className="type-display-3">Fans &amp; Haters</h1>
      {lede && <p className="type-editorial likes__lede">{lede}</p>}
    </header>
  );
}

export function LikesScreen() {
  const [tab, setTab] = useState<Tab>("fans");
  const [fans, setFans] = useState<GridPage | null>(null);
  const [haters, setHaters] = useState<GridPage | null>(null);
  const [reveal, setReveal] = useState<SwipeResult | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const { send, busy } = useSwipeAction();

  const load = useCallback(async () => {
    try {
      const [f, h] = await Promise.all([fetchPage("fans"), fetchPage("haters")]);
      setFans(f);
      setHaters(h);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
      else toast(resolveCopy("errors.offline") ?? "Offline.", "error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // EC-38: premium lapse/grant mid-view → refetch; the server re-blurs (or
  // un-blurs) on the very next payload — the push is only the re-render hint.
  useEffect(() => ws.on("premium_changed", () => void load()), [load]);

  async function loadMore(which: Tab) {
    const page = which === "fans" ? fans : haters;
    const cursor = page?.next_cursor;
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await fetchPage(which, cursor);
      const merge = (prev: GridPage | null): GridPage => ({
        ...(prev ?? {}),
        blurred: next.blurred,
        total: next.total,
        entries: [...(prev?.entries ?? []), ...(next.entries ?? [])],
        next_cursor: next.next_cursor,
      });
      if (which === "fans") setFans(merge);
      else setHaters(merge);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setLoadingMore(false);
    }
  }

  async function act(user: UserRef | undefined, verdict: "like" | "reject") {
    const id = user?.id;
    if (!id || busy) return;
    const out = await send({ targetId: id, verdict });
    if (out.kind === "cap") {
      toast(resolveCopy("deck.cap_reached") ?? "Out of swipes.", "error");
      return;
    }
    if (out.kind === "error" || out.kind === "interstitial") return;
    // resolved or gone — either way the pair left this surface (SM-1).
    const drop = (p: GridPage | null): GridPage | null => {
      if (!p?.entries?.some((e) => e.user?.id === id)) return p;
      return {
        ...p,
        total: Math.max(0, (p.total ?? 1) - 1),
        entries: p.entries.filter((e) => e.user?.id !== id),
      };
    };
    setFans(drop);
    setHaters(drop);
    if (out.kind === "resolved" && out.result?.result.startsWith("matched_")) {
      setReveal(out.result);
    }
  }

  if (fans === null || haters === null) {
    return (
      <div>
        <Head />
        <Skeleton height={44} radius={22} style={{ marginBottom: 14 }} />
        <div className="grid-cards" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={210} radius={14} />
          ))}
        </div>
      </div>
    );
  }

  // #37 blurred teaser — one gate for the whole screen (both tabs are blurred
  // together; identities are absent from both payloads).
  if (fans.blurred) {
    const fanCount = fans.total ?? 0;
    const hateCount = haters.total ?? 0;
    return (
      <motion.div variants={stagger} initial="initial" animate="enter">
        <motion.div variants={rise}>
          <Head lede={resolveCopy("likes.blur_lede")} />
        </motion.div>
        <motion.div variants={rise}>
          <Card padding="lg" className="likes__blur-teaser">
            <div className="likes__teaser-counts" aria-hidden="true">
              <span className="likes__teaser-count likes__teaser-count--fans">
                <span className="likes__teaser-num">{fanCount}</span>
                <span className="likes__teaser-word">Fans</span>
              </span>
              <span className="likes__teaser-count likes__teaser-count--haters">
                <span className="likes__teaser-num">{hateCount}</span>
                <span className="likes__teaser-word">Haters</span>
              </span>
            </div>
            <p className="likes__teaser-line">
              {formatCopy(resolveCopy("likes.blur_teaser") ?? "", { n: fanCount, m: hateCount })}
            </p>
            <p className="type-editorial likes__teaser-pitch">
              {resolveCopy("store.premium_pitch")}
            </p>
            <Link to="/store" className="btn btn--primary">
              Go Premium
            </Link>
          </Card>
        </motion.div>
        <motion.div className="grid-cards" aria-hidden="true" variants={rise}>
          {Array.from({ length: Math.min(9, Math.max(3, fanCount + hateCount)) }).map((_, i) => (
            <div key={i} className="grid-card grid-card--ghost">
              <div className="grid-card__photo grid-card__ghost" />
              <span className="grid-card__lock">
                <IconBrokenHeart width={22} height={22} />
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    );
  }

  const page = tab === "fans" ? fans : haters;
  const entries = page.entries ?? [];
  const rail = fans.viewers_rail ?? [];

  return (
    <div>
      <Head lede={resolveCopy("likes.lede")} />

      <div className="tabs likes__tabs" role="tablist" aria-label="Fans and haters">
        <Button
          className="tabs__tab"
          variant={tab === "fans" ? "primary" : "ghost"}
          role="tab"
          aria-selected={tab === "fans"}
          onClick={() => setTab("fans")}
        >
          Fans · {fans.total ?? 0}
        </Button>
        <Button
          className="tabs__tab"
          variant={tab === "haters" ? "primary" : "ghost"}
          role="tab"
          aria-selected={tab === "haters"}
          onClick={() => setTab("haters")}
        >
          Haters · {haters.total ?? 0}
        </Button>
      </div>

      {tab === "fans" && rail.length > 0 && (
        <section aria-label="Recent viewers">
          <h2 className="likes__railtitle">Recent viewers</h2>
          <div className="rail">
            {rail.map((u) => (
              <div key={u.id} className="rail__item">
                <div className="rail__avatar">
                  <Avatar user={u} />
                </div>
                <span className="likes__railname">{u.display_name ?? "Someone"}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "haters" && (haters.total ?? 0) > 0 && (
        <p className="likes__strap">Your haters await.</p>
      )}

      {entries.length === 0 ? (
        <Card padding="lg" className="likes__empty">
          <Mascot
            mood="ko"
            quip={resolveCopy(tab === "fans" ? "likes.fans_empty" : "likes.haters_empty") ?? ""}
          />
        </Card>
      ) : (
        <>
          <motion.div
            className="grid-cards"
            key={tab}
            variants={stagger}
            initial="initial"
            animate="enter"
          >
            {entries.map((e, i) => (
              <motion.div key={e.user?.id ?? `row-${i}`} variants={rise}>
                <GridCell entry={e} busy={busy} onAct={(v) => void act(e.user, v)} />
              </motion.div>
            ))}
          </motion.div>
          {page.next_cursor && (
            <Button
              block
              variant="ghost"
              style={{ marginTop: 10 }}
              loading={loadingMore}
              onClick={() => void loadMore(tab)}
            >
              More
            </Button>
          )}
        </>
      )}

      {/* S-12 match reveal — acting from the grid can complete a pair (SM-1) */}
      <Sheet open={reveal !== null} onClose={() => setReveal(null)}>
        {reveal && (
          <div className={`reveal${reveal.result === "matched_reject" ? " reveal--reject" : ""}`}>
            <h2 className="h-display reveal__title">
              {reveal.reveal?.copy_key
                ? resolveCopy(reveal.reveal.copy_key)
                : resolveCopy("match.classic_title")}
            </h2>
            <p className="text-dim">
              {resolveCopy(
                reveal.result === "matched_reject" ? "match.reject_body" : "match.classic_body",
              )}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Link to="/matches" className="btn btn--primary" style={{ flex: 1 }}>
                Say something
              </Link>
              <Button variant="ghost" style={{ flex: 1 }} onClick={() => setReveal(null)}>
                Keep browsing
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

function Avatar({ user }: { user: UserRef }) {
  const [ok, setOk] = useState(true);
  // Server-signed viewer-bound URL (plain <img> renderable — no bearer needed).
  const src = user.avatar_url ?? null;
  if (src && ok) {
    return <img src={src} alt="" onError={() => setOk(false)} />;
  }
  return (
    <div className="likes__fallback" aria-hidden="true">
      {(user.display_name ?? "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function GridCell({
  entry,
  busy,
  onAct,
}: {
  entry: GridEntry;
  busy: boolean;
  onAct: (verdict: "like" | "reject") => void;
}) {
  const u = entry.user ?? {};
  return (
    <div className="grid-card">
      <div className="grid-card__photo">
        <Avatar user={u} />
      </div>
      <div className="grid-card__body">
        <div className="face__name">
          <strong>{u.display_name ?? "Someone"}</strong>
          {u.verified && <VerifiedBadge />}
        </div>
        <VerdictLine verdict={entry.verdict} at={entry.at} />
        {/* Equal-weight verdict pair — rejection is a first-class verb (§2). */}
        <div className="grid-card__actions">
          <Button
            variant="reject"
            size="sm"
            ripple={false}
            style={{ flex: 1 }}
            disabled={busy}
            title={resolveCopy("deck.swipe_labels.left")}
            onClick={() => onAct("reject")}
          >
            ✕
          </Button>
          <Button
            variant="like"
            size="sm"
            ripple={false}
            style={{ flex: 1 }}
            disabled={busy}
            title={resolveCopy("deck.swipe_labels.right")}
            onClick={() => onAct("like")}
          >
            ♥
          </Button>
        </div>
      </div>
    </div>
  );
}

function VerdictLine({ verdict, at }: { verdict: string | undefined; at?: string }) {
  const cls =
    verdict === "crush"
      ? "likes__verdict likes__verdict--crush"
      : verdict === "reject"
        ? "likes__verdict likes__verdict--reject"
        : "likes__verdict likes__verdict--like";
  const text =
    verdict === "crush"
      ? `⭐ ${resolveCopy("deck.crush_ribbon") ?? "CRUSHED ON YOU"}`
      : verdict === "reject"
        ? "✕ Rejected you"
        : "♥ Liked you";
  return (
    <div className={cls}>
      {text}
      {at ? <span className="likes__ago"> · {ago(at)}</span> : null}
    </div>
  );
}

/** Compact relative timestamp for grid rows — display only. */
function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "now";
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return "now";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
